import React from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Col,
    ControlLabel,
    DropdownButton,
    FormControl,
    FormGroup,
    InputGroup,
    MenuItem,
    Row,
    Well,
} from 'react-bootstrap';
import Datetime from 'react-datetime';
import moment from 'moment';
import map from 'lodash/map';
import { ABNORMALITIES, I18N } from '../../../constants';
import { formatStopLocation } from '../../../shared/utils/run';

const handleValidDate = currentDate => currentDate.isBefore(moment());
const AbnormalityInfo = ({
    isAnalysis,
    isEditable,
    isAbnormalityDateEditable,
    isCreation,
    abnormalityType,
    abnormalityStatus,
    abnormalityStop,
    trailerIdentifier,
    stops,
    selectedDate,
    selectedStop,
    onDateChange,
    onResetDate,
    onTrailerIdentifierChange,
    onStopChange,
    validations,
}) => (
    <Well>
        {!isCreation && (
            <Row>
                <Col sm={6}>
                    <Row style={{ marginBottom: 10 }}>
                        <Col componentClass={ControlLabel} sm={4}>
                            Abnormality Type
                        </Col>
                        <Col sm={8}>
                            <b>{abnormalityType ? I18N.ABNORMALITIES[abnormalityType] : '-'}</b>
                        </Col>
                    </Row>
                </Col>
            </Row>
        )}
        {!isCreation && (
            <Row>
                <Col sm={6}>
                    <Row style={{ marginBottom: 10 }}>
                        <Col componentClass={ControlLabel} sm={4} style={{ marginTop: 5 }}>
                            Abnormality Status
                        </Col>
                        <Col sm={8}>
                            <b style={{ marginTop: 5 }}>
                                {abnormalityStatus
                                    ? I18N.ABNORMALITY_STATUS[abnormalityStatus]
                                    : '-'}
                            </b>
                        </Col>
                    </Row>
                </Col>
            </Row>
        )}
        <Row>
            <Col sm={6}>
                <Row style={{ marginBottom: 10 }}>
                    <Col componentClass={ControlLabel} sm={4} style={{ marginTop: 5 }}>
                        Abnormality Date {isCreation || isAbnormalityDateEditable ? '*' : ''}
                    </Col>
                    <Col sm={8}>
                        {!isAbnormalityDateEditable ? (
                            <p style={{ marginTop: 5 }}>
                                {selectedDate.format('DD-MM-YYYY HH:mm') || '-'}
                            </p> // if it's not editable then take the string of the moment date
                        ) : (
                            <div>
                                <FormGroup className="no-vertical-margin">
                                    <InputGroup>
                                        <Datetime
                                            className={validations.date && 'datetime-error'}
                                            value={selectedDate}
                                            onChange={onDateChange('selectedDate')}
                                            dateFormat="DD-MM-YYYY"
                                            timeFormat="HH:mm"
                                            inputProps={{ maxLength: 255 }}
                                            isValidDate={handleValidDate}
                                        />
                                        {isAbnormalityDateEditable && (
                                            <InputGroup.Button>
                                                <Button bsStyle="primary" onClick={onResetDate}>
                                                    Set to now
                                                </Button>
                                            </InputGroup.Button>
                                        )}
                                    </InputGroup>
                                </FormGroup>
                                {validations.date && (
                                    <p style={{ color: '#CF142B', margin: 5 }}>
                                        {validations.date}
                                    </p>
                                )}
                            </div>
                        )}
                    </Col>
                </Row>
            </Col>
        </Row>
        <Row>
            <Col sm={6}>
                <Row style={{ marginBottom: 10 }}>
                    <Col componentClass={ControlLabel} sm={4} style={{ marginTop: 5 }}>
                        {abnormalityType === ABNORMALITIES.DELAY
                            ? 'Next Logistic Point'
                            : 'Abnormality Location'}{' '}
                        {isCreation || isEditable ? '*' : ''}
                    </Col>
                    <Col sm={8}>
                        {isCreation || (isEditable && abnormalityType === ABNORMALITIES.DELAY) ? (
                            <div>
                                <DropdownButton
                                    id="abnormalityLocationDropdown"
                                    title={selectedStop}
                                    style={
                                        validations.stop && {
                                            borderColor: '#CF142B',
                                        }
                                    }
                                    onSelect={onStopChange}
                                >
                                    {map(stops, stop => (
                                        <MenuItem
                                            key={stop.stopId}
                                            eventKey={stop.stopId}
                                            active={selectedStop === formatStopLocation(stop)}
                                        >
                                            {formatStopLocation(stop)}
                                        </MenuItem>
                                    ))}
                                </DropdownButton>
                                {validations.stop && (
                                    <p
                                        style={{
                                            color: '#CF142B',
                                            margin: 5,
                                        }}
                                    >
                                        {validations.stop}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <p style={{ marginTop: 5 }}>{abnormalityStop || '-'}</p>
                        )}
                    </Col>
                </Row>
            </Col>
        </Row>
        <Row>
            <Col sm={6}>
                <Row style={{ marginBottom: 0 }}>
                    <Col componentClass={ControlLabel} sm={4} style={{ marginTop: 5 }}>
                        Trailer Number
                    </Col>
                    <Col sm={8}>
                        {!isEditable ? (
                            <p style={{ marginTop: 5 }}>{trailerIdentifier || '-'}</p>
                        ) : (
                            <FormControl
                                type="text"
                                maxLength="1000"
                                placeholder="e.g. 1-ABC-123"
                                value={trailerIdentifier}
                                onChange={onTrailerIdentifierChange}
                            />
                        )}
                        {validations &&
                            validations.trailerIdentifier && (
                                <p style={{ color: '#CF142B', margin: 5 }}>
                                    {validations.trailerIdentifier}
                                </p>
                            )}
                    </Col>
                </Row>
            </Col>
        </Row>
    </Well>
);

AbnormalityInfo.propTypes = {
    isAnalysis: PropTypes.bool,
    isEditable: PropTypes.bool,
    isAbnormalityDateEditable: PropTypes.bool,
    isCreation: PropTypes.bool,
    abnormalityType: PropTypes.string,
    abnormalityStatus: PropTypes.string,
    abnormalityStop: PropTypes.string,
    trailerIdentifier: PropTypes.string,
    stops: PropTypes.array,
    selectedDate: PropTypes.object,
    selectedStop: PropTypes.string,
    onDateChange: PropTypes.func,
    onResetDate: PropTypes.func,
    onTrailerIdentifierChange: PropTypes.func,
    onStopChange: PropTypes.func,
    validations: PropTypes.object,
};

export default AbnormalityInfo;
